<script setup lang="ts">
import SimpleOverlay from "@/components/map/SimpleOverlay.vue";
import ObjectSelection from "@/components/map/object-selection/ObjectSelection.vue";
import { MarkerPosition, useMarkerManager } from "@/components/map/use/use-marker-manager";
import Drawer from "@/layouts/drawer/Drawer.vue";
import DrawerLayout from "@/layouts/drawer/DrawerLayout.vue";
import { DrawerSelection } from "@/layouts/drawer/use-drawer-data";
import { useDrawerStore, useMapStore } from "@/stores";
import {
	GoogleMap,
	GoogleMapMarker,
	GoogleMapMarkerCluster,
	GoogleMapOverlay,
	Marker,
	useExposedMap,
	type onClusterClickHandler,
} from "@soft-db/google-maps";
import { useScreenSize } from "@soft-db/ui";
import { storeToRefs } from "pinia";
import { computed, reactive, ref } from "vue";
import { useRouter } from "vue-router";

type MapQuery = {
	id: string;
	type: string;
};

const { map, attrs: mapAttrs } = useExposedMap();

const router = useRouter();

const drawerStore = useDrawerStore();
const { mdOrLower } = useScreenSize();

const options = computed<google.maps.MapOptions>(() => ({
	fullscreenControl: false,
	panControl: false,
	zoomControl: false,
	streetViewControl: false,
	gestureHandling: "greedy",
	maxZoom: 20,
	disableDefaultUI: false,
	mapTypeControlOptions: {
		position: 3,
	},
}));
const zoom = ref(undefined);
const center = ref<google.maps.LatLng>();
const { mapId, mapTypeId } = storeToRefs(useMapStore());

const syncOptions = reactive({ zoom, center, mapTypeId });

const { state, markers, activeMarker } = useMarkerManager(map);

const clusterMarkers = computed(() => {
	return markers.value.filter((m) => m !== activeMarker.value);
});

const route = useRoute();

const updateView = (newQuery?: MapQuery) => {
	let query = { ...route.query };

	if (newQuery) {
		query = { ...query, ...newQuery };
	} else {
		if (!state.value.id) return;

		delete query.id;
		delete query.type;
	}

	router.push({
		query,
	});
};

const markerClicked = (marker: Marker) => {
	// Manually calling mouse out, since on click prevent the mouse out event to trigger.
	markerMouseOut();

	const { id, type } = state.value;

	if (marker.meta == null) {
		return;
	}

	if (marker.meta.id !== id || marker.meta.type !== type) {
		updateView({ id: marker.meta.id, type: marker.meta.type });
	}
};

function updateSelection(drawerSelection: DrawerSelection) {
	const { id, type } = state.value;
	if (drawerSelection.id !== id || drawerSelection.type !== type) {
		updateView({ id: drawerSelection.id, type: drawerSelection.type.toString() });
	}

	if (mdOrLower.value) {
		drawerStore.toggleDrawer();
	}
}
function unselect() {
	updateView();
}

const markerOver = ref<Marker>();

const markerMouseOver = (marker: Marker) => {
	markerOver.value = marker;
};

const markerMouseOut = () => {
	markerOver.value = undefined;
};

const bounds = ref<google.maps.LatLngBounds | null>();
const updateBounds = () => {
	bounds.value = map.value?.getBounds();
};

const inBounds = (position: MarkerPosition) => {
	if (bounds.value) {
		return bounds.value.contains(position);
	}
	return true;
};

const isActive = (marker: Marker) => {
	const { id, type } = state.value;
	return marker.meta?.id === id?.toString() && marker.meta?.type === type;
};
const isHidden = (marker: Marker) => {
	if (isActive(marker)) return false;

	if (state.value.id) return true;
	return !inBounds(marker.position);
};

const showMarkerOverlay = (marker: Marker) => {
	if (syncOptions.zoom == null) {
		return true;
	}

	return (
		(!isHidden(marker) && syncOptions.zoom >= 10) ||
		(markerOver.value?.meta?.id === marker.meta?.id &&
			markerOver.value?.meta?.type === marker.meta?.type)
	);
};

const onClusterClick: onClusterClickHandler = (_, cluster, map) => {
	if (cluster.bounds != null) {
		map.fitBounds(cluster.bounds, 128);
	}
};
</script>

<template>
	<DrawerLayout :forceOpen="!mdOrLower">
		<template #drawer>
			<Drawer class="h-full" headers @click="updateSelection" />
		</template>
		<GoogleMap
			v-bind="mapAttrs"
			v-model:zoom="syncOptions.zoom"
			v-model:center="syncOptions.center"
			v-model:mapTypeId="syncOptions.mapTypeId"
			class="bg-transparent"
			:mapId="mapId"
			:options="options"
			@bounds_changed="updateBounds()"
		>
			<GoogleMapMarker
				v-if="activeMarker"
				id="active-marker"
				:key="activeMarker.id"
				v-slot="{ position }"
				:position="activeMarker.position"
				:icon="activeMarker.icon"
				:meta="activeMarker.meta"
			>
				<GoogleMapOverlay v-if="activeMarker.meta" :position="position">
					<SimpleOverlay
						:name="activeMarker.meta.name"
						:image="activeMarker.meta.image || ''"
						:sourceType="activeMarker.meta.type"
						:sourceId="activeMarker.meta.id"
					/>
				</GoogleMapOverlay>
			</GoogleMapMarker>

			<GoogleMapMarkerCluster
				:maxZoom="15"
				:markers="clusterMarkers"
				@click:marker="(_, marker, __) => markerClicked(marker)"
				@click:cluster="onClusterClick"
				@mouseover:marker="(_, marker) => markerMouseOver(marker)"
				@mouseout:marker="() => markerMouseOut()"
			>
				<template #overlay="{ marker }">
					<GoogleMapOverlay
						v-if="marker.meta && showMarkerOverlay(marker)"
						:position="marker.position"
					>
						<SimpleOverlay
							:name="marker.meta.name"
							:image="marker.meta.image || ''"
							:sourceType="marker.meta.type"
							:sourceId="marker.meta.id"
						/>
					</GoogleMapOverlay>
				</template>
			</GoogleMapMarkerCluster>
		</GoogleMap>
		<ObjectSelection
			v-if="activeMarker && activeMarker.meta"
			:id="activeMarker.meta.id"
			:objectType="activeMarker.meta.type"
			class="absolute"
			:class="mdOrLower ? 'bottom-0 left-0 right-0' : 'left-75 top-3 w-80'"
			@close="unselect"
		/>
	</DrawerLayout>
</template>

<style>
#map > div:first-child {
	background-color: oklch(var(--b1)) !important;
}

#map iframe {
	opacity: 0;
	background-color: oklch(var(--b1)) !important;
}

.gm-style-iw .gm-style-iw-c .gm-style {
	border: 0;
	padding: 0 !important;
	margin: 0;
}

.bottom-drawer-tooltip {
	height: auto !important;
}

.gm-style iframe + div {
	border: none !important;
}
</style>
